import {useCallback, useEffect, useId, useMemo, useRef, useState} from "react";
import { adCopyIssues, type AdCopyField } from "src/shared/utils/validation/networkTextRules";
import {Ad, contentEditAllowed} from "@models/Ad.model";
import {BrandAssets} from "@models/brand.model";
import {ContentCatalogService} from "@services/contentCatalog/ContentCatalogService";
import {MediaService} from "@services/media/MediaService";
import type {ResolvedAdContent} from "@services/media/commands/cmdResolveAds";
import type {CreateAdPayload} from "@services/contentCatalog/commands/cmdCreateAd";
import {capitalizeFirstLetter} from "../../shared/utils/common";
import MotiModal from "src/shared/components/MotiModal";
import { AdThumb } from "@/shared/components/AdCard/AdThumb";
import CoverPicker from "@components/CoverPicker/CoverPicker";
import * as ContextMenu from "@radix-ui/react-context-menu";
import ConfirmActionModal from "../contentCatalog/components/ConfirmActionModal";
import RenameActionModal from "../contentCatalog/components/RenameActionModal";
import { showMessage } from "../createV3/layerStore";
import ResizableSplitView from "src/shared/components/ResizableSplitView/ResizableSplitView";
import ReviewCommentList from "./components/ReviewCommentList";
import AdTagsReadOnly from "./components/AdTagsReadOnly";
import CustomizationRequests from "./components/CustomizationRequests";
import AdTagsPanel from "./components/AdTagsPanel";
import DebugAdTreeModal from "./components/DebugAdTreeModal";
import AdLockedDialog from "./components/AdLockedDialog";
import { appStore } from "@store/appStore";
import { PermissionLevels } from "@models/permission.model";
import ApprovedUrlPicker from "@components/ApprovedUrlPicker/ApprovedUrlPicker";
import { ApprovedUrl } from "@models/approvedUrl.model";
import { nextLinkDescription } from "./linkDescriptionPrefill";
import AdTokenTextarea, { type AdTokenTextareaHandle } from "./components/AdTokenTextarea";
import AdCopyTokenNotes from "./components/AdCopyTokenNotes";
import AdTokenMessages, { AD_TOKEN_CHECKS_UNAVAILABLE } from "@/shared/adTokens/AdTokenMessages";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import { containsAdTokenSyntax } from "@/shared/adTokens/adTokenScan";
import { AD_TOKEN_COPY_FIELDS, useAdCopyHeaderNotes } from "./useAdCopyHeaderNotes";
import AutoGrowTextarea from "@/shared/components/AutoGrowTextarea";
import { AD_COPY_AD_TOKENS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import DirectMailForm from "./components/DirectMailForm";
import EmailForm from "./components/EmailForm";
import SmsForm from "./components/SmsForm";
import { convertLegacyLayer } from "../kinema/layeredMediaService";
import { navigate } from "../../navigate";
import "./createEditorModal.css";
import { RecordId } from "@/shared/components/recordId/RecordId";

interface CreateProps {
    ad?: Ad | null;
    adId?: number;
    brandAssets?: BrandAssets;
    adMode?: boolean;
    modalOpen?: boolean;
    setModalOpen?: (isOpen: boolean) => void;
    onCreate?: (parentId: number, templateName: string) => void;
    onDuplicate?: (parentId: number) => void;
    onDelete?: (id: number) => void;
    onRename?: (id: number, name: string) => void;
    hideX?: boolean;
    launchId?: number;
    organizationId?: number;
    /** Set when opened from an ad group — a fork then replaces the ad in that ad group
     *  instead of only landing in the catalog. */
    adGroupId?: number;
}

type ChildContainerSubtype = "video" | "image";
// A mail piece, an email and a text are created as LEAVES rather than containers: none of
// them has variants, so there is nothing for a container to hold (MPN-1706, MPN-1716,
// MPN-1717).
type ChildAdType = ChildContainerSubtype | "direct_mail" | "email" | "sms";

// Types that are a piece of content rather than a container holding one. A postcard, an
// email and a text carry their own body, so there is nothing for them to contain, and
// creating them as containers would leave an empty box the author has to open before they
// can write anything.
const LEAF_TYPES = new Set<ChildAdType>(["direct_mail", "email", "sms"]);
const LEAF_NAMES: Record<string, string> = {
    direct_mail: "Mail piece",
    email: "Email",
    sms: "Text message",
};

function collectAdIdsFromHierarchy(ad: Ad): number[] {
    const ids = [ad.id];
    for (const child of ad.children || []) {
        ids.push(...collectAdIdsFromHierarchy(child));
    }
    return ids;
}

// Opened in Create Studio: a composition with layers to manipulate.
const EDITABLE_TYPES = new Set(["video", "image"]);

// Edited in place instead. These carry their own body -- artwork, a subject and HTML, a line
// of text -- so there is no composition to open and no variants to sit above.
const LEAF_CONTENT_FORMS: Record<string, React.ComponentType<{
    ad: { id: number; name?: string | null };
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
    /** Only the mail piece uses it, to list the Kinema designs it can import. */
    organizationId?: number | null;
    /** Mail piece only: lay one side out in Kinema rather than picking a finished design. */
    onEditSide?: (side: "front" | "back") => void;
}>> = {
    direct_mail: DirectMailForm,
    email: EmailForm,
    sms: SmsForm,
};

function isEditableAd(ad: Ad): boolean {
    return EDITABLE_TYPES.has(ad.type);
}

function adTypeLabel(ad: Ad): string {
    if (ad.type === "container" && ad.subtype) return `container / ${ad.subtype}`;
    if (ad.subtype) return `${ad.type} / ${ad.subtype}`;
    return ad.type;
}

function containerTitle(ad: Ad): string {
    const subtype = ad.subtype?.toLowerCase();
    if (subtype && subtype !== "container") {
        return capitalizeFirstLetter(subtype);
    }
    return ad.name || `Ad ${ad.id}`;
}

function addChildToTree(root: Ad | null, parentId: number, child: Ad): Ad | null {
    if (!root) return root;
    const nextRoot = new Ad(root);

    const walk = (node: Ad): boolean => {
        if (node.id === parentId) {
            node.children = [...(node.children || []), child];
            return true;
        }
        for (const nextChild of node.children || []) {
            if (walk(nextChild)) return true;
        }
        return false;
    };

    walk(nextRoot);
    return nextRoot;
}

// ─── Ad Copy Form (inline in accordion) ──────────────────
export function AdCopyForm({ leafAds, requestEdit, organizationId }: {
    leafAds: Ad[];
    /** CreateEditorModal owns the content lock — see requestEdit there. */
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
    /** Whose Approved URLs the Link picker offers. */
    organizationId?: number;
}) {
    const defaultAd = leafAds.find((c) => c.is_default) ?? leafAds[0];
    const [copyFields, setCopyFields] = useState<Record<string, string>>({});
    const [loaded, setLoaded] = useState(false);
    // A failed read used to set `loaded` and leave the fields blank, so the panel
    // showed every field at 0 characters -- indistinguishable from an ad with no copy,
    // and wrong in the one place it matters: arriving from a publish error ABOUT the
    // copy. Say the read failed rather than assert the ad is empty.
    const [loadFailed, setLoadFailed] = useState(false);
    // Arriving from a failed publish's fix link, the copy IS the errand -- landing on a
    // collapsed section hides the field the error is about. Read the flag off the
    // location rather than useSearchParams: some hosts render this editor outside the
    // engagement route entirely (see useItemRoute), where the router hooks throw.
    const [open, setOpen] = useState(() => {
        try {
            return new URLSearchParams(window.location.search).get("focus") === "copy";
        } catch {
            return false;
        }
    });
    const ccService = useMemo(() => new ContentCatalogService(), []);
    // Off, the fields are the plain ones they were before ad tokens, and nothing is read (MPN-2014).
    const adTokensOn = useFeatureFlag(AD_COPY_AD_TOKENS);
    // One vocabulary for all four fields, read once per page load (MPN-1932).
    const adTokens = useAdTokens(adTokensOn);
    const tokenFields = useRef<Record<string, AdTokenTextareaHandle | null>>({});
    const fieldId = useId();
    const copyFieldsRef = useRef(copyFields);
    copyFieldsRef.current = copyFields;
    // The description last filled in from a link, to tell it apart from the writer's edits.
    const lastPrefilledRef = useRef('');

    // Saved copy's ad token warnings for the collapsed header, computed by the server (MPN-2192).
    // The copy itself is read only when the section opens.
    const headerNotes = useAdCopyHeaderNotes(defaultAd.id, adTokensOn);

    useEffect(() => {
        if (!open || loaded) return;
        ccService.getAd(defaultAd.id, ["content"]).then((ad) => {
            const c = (ad.content ?? {}) as Record<string, any>;
            setCopyFields({
                copy_text: c.copy_text ?? '',
                link: c.link ?? '',
                link_headline: c.link_headline ?? '',
                link_description: c.link_description ?? '',
                cta_text: c.cta_text ?? '',
            });
            setLoaded(true);
        }).catch(() => {
            setLoadFailed(true);
            setLoaded(true);
        });
    }, [open, loaded, defaultAd.id, ccService]);

    const saveContent = async (force: boolean) => {
        const copyOnly = copyFieldsRef.current;
        // Preserve each sibling's media/layer fields: fetch that leaf's current
        // content, overlay only the copy fields, PATCH back. Sending one shared
        // content blob would otherwise overwrite each sibling's media with the
        // default leaf's media.
        await Promise.all(
            leafAds.map(async (leaf) => {
                try {
                    const latest = await ccService.getAd(leaf.id, ["content"]);
                    const current = (latest.content ?? {}) as Record<string, any>;
                    const next = { ...current, ...copyOnly };
                    await ccService.updateAd(leaf.id, { content: next as any, force_edit: force });
                } catch { /* non-critical, skip this leaf */ }
            }),
        );
        headerNotes.refresh();
    };

    const handleSelectLink = (url: string, selected?: ApprovedUrl) => {
        // saveContent reads the ref, which state alone would leave a render behind.
        const next: Record<string, string> = { ...copyFieldsRef.current, link: url };
        // Prefill before the save below, so the description persists with the link.
        if (selected) {
            const description = nextLinkDescription(next.link_description ?? '', lastPrefilledRef.current, selected.title);
            if (description !== null) {
                next.link_description = description;
                lastPrefilledRef.current = description;
            }
        }
        copyFieldsRef.current = next;
        setCopyFields(next);
        requestEdit(saveContent);
    };

    const fields = [
        { key: 'copy_text', label: 'Body Copy' },
        { key: 'link', label: 'Link' },
        { key: 'link_headline', label: 'Headline' },
        { key: 'link_description', label: 'Description' },
        { key: 'cta_text', label: 'CTA Text' },
    ];

    // Said once for the panel, and only when there is something it could not check: with no
    // vocabulary every bracket would be "unknown", so the field notes stay silent instead
    // (MPN-1930).
    const tokenChecksUnavailable = adTokensOn && adTokens.status === 'unavailable'
        && AD_TOKEN_COPY_FIELDS.some((key) => containsAdTokenSyntax(copyFields[key] || ''));
    // Collapsed only: open, each field says its own warnings (MPN-2072).
    const headerWarnings = adTokensOn && !open ? headerNotes.warningCount : 0;

    return (
        <div style={{ marginBottom: 8 }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    background: 'none', border: '1px solid #d7dde8', borderRadius: 6,
                    padding: '5px 10px', fontSize: 12, color: '#4f596a', cursor: 'pointer',
                    width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6,
                }}
            >
                <span style={{ fontSize: 10, color: '#6c7585' }}>{open ? 'v' : '>'}</span>
                Ad Copy
                {headerWarnings > 0 ? (
                    <span style={{ color: '#dc2626' }}>
                        · {headerWarnings} ad token warning{headerWarnings === 1 ? '' : 's'}
                    </span>
                ) : adTokensOn && !open && headerNotes.unavailable && (
                    <span style={{ color: '#64748b' }}>· {AD_TOKEN_CHECKS_UNAVAILABLE}</span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>
                    {defaultAd.name || `Ad ${defaultAd.id}`}
                </span>
            </button>
            {open && (
                <div style={{
                    border: '1px solid #e1e6ef', borderTop: 'none', borderRadius: '0 0 6px 6px',
                    padding: 10, display: 'flex', flexDirection: 'column', gap: 8, background: '#fff',
                }}>
                    {!loaded ? (
                        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 8 }}>Loading...</div>
                    ) : loadFailed ? (
                        <div style={{ fontSize: 12, color: '#b42318', textAlign: 'center', padding: 8 }}>
                            Could not load this ad's copy. Reload to try again.
                        </div>
                    ) : (
                        <>
                        <AdTokenMessages notes={[]} unavailable={tokenChecksUnavailable} style={{ fontSize: 11 }} />
                        {fields.map(({ key, label }) => {
                            const value = copyFields[key] || '';
                            // Derived from the value on every render, which is what makes
                            // this retroactive: copy saved before these rules existed shows
                            // its errors the moment the panel opens, with no edit needed
                            // (MPN-1616).
                            const issues = key === 'link'
                                ? []
                                : adCopyIssues(key as AdCopyField, value);
                            const commonProps = {
                                value,
                                onValueChange: (next: string) =>
                                    setCopyFields((prev) => ({ ...prev, [key]: next })),
                                onBlur: () => requestEdit(saveContent),
                                style: {
                                    width: '100%', padding: '5px 8px',
                                    border: `1px solid ${issues.length ? '#dc2626' : '#d7dde8'}`,
                                    borderRadius: 4, fontSize: 12, outline: 'none', color: '#2f3440',
                                    boxSizing: 'border-box' as const,
                                    fontFamily: 'inherit',
                                },
                            };
                            // The plain field takes a change event, as it did before ad tokens.
                            const { onValueChange, ...plainProps } = commonProps;
                            return (
                                <div key={key}>
                                    {/* Pointed at its field: the label holds the "Use [TERM]" fix, and a
                                        label with no `for` labels the first control inside it --
                                        clicking the title would rewrite the token. */}
                                    <label
                                        htmlFor={key === 'link' || !adTokensOn ? undefined : `${fieldId}-${key}`}
                                        style={{ fontSize: 11, color: '#6c7585', display: 'block', marginBottom: 2 }}
                                    >
                                        {label}
                                        {/* Count is informational only, with no limit beside
                                            it: the limits differ per network, so showing one
                                            number would be wrong for every other network. */}
                                        {key !== 'link' && (
                                            <span style={{ marginLeft: 6, color: "#94a3b8" }}>{value.length} characters</span>
                                        )}
                                        {/* Beside the title rather than under the field, so a
                                            long message cannot push the next field down the
                                            panel as the user types. */}
                                        {issues.map((issue: string) => (
                                            <span key={issue} style={{ marginLeft: 6, color: '#dc2626' }}>{issue}</span>
                                        ))}
                                        {key !== 'link' && adTokensOn && (
                                            <AdCopyTokenNotes
                                                value={value}
                                                onFix={(change) => tokenFields.current[key]?.edit(change)}
                                            />
                                        )}
                                    </label>
                                    {key === 'link' ? (
                                        <ApprovedUrlPicker
                                            organizationId={organizationId ?? null}
                                            value={copyFields.link || ''}
                                            onChange={handleSelectLink}
                                            label=""
                                        />
                                    ) : !adTokensOn ? (
                                        // Every copy field grows to fit, so none of it can sit out
                                        // of sight unreviewed (MPN-1710). The short ones were inputs,
                                        // which hide overflow sideways the same way.
                                        <AutoGrowTextarea
                                            {...plainProps}
                                            onChange={(e) => onValueChange(e.target.value)}
                                            rows={key === 'copy_text' ? 4 : 1}
                                            singleLine={key !== 'copy_text'}
                                        />
                                    ) : (
                                        // Typing `[` here offers the ad token vocabulary (MPN-1932).
                                        <AdTokenTextarea
                                            {...commonProps}
                                            ref={(handle) => { tokenFields.current[key] = handle; }}
                                            id={`${fieldId}-${key}`}
                                            tokens={adTokens.tokens}
                                            rows={key === 'copy_text' ? 4 : 1}
                                            singleLine={key !== 'copy_text'}
                                        />
                                    )}
                                </div>
                            );
                        })}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export function AdHierarchyNode({
                             ad,
                             depth = 0,
                             loadingAdId,
                             selectedAdId,
                             onSelectAd,
                             onAddChild,
                             onRenameAd,
                             onDeleteAd,
                             onDuplicateAd,
                             renamingAdId,
                             deletingAdId,
                             duplicatingAdId,
                             creatingChildForParentId,
                             resolvedMedia,
                             requestEdit,
                             organizationId,
                         }: {
    ad: Ad;
    depth?: number;
    loadingAdId: number | null;
    selectedAdId: number | null;
    onSelectAd: (ad: Ad, side?: "front" | "back") => void;
    onAddChild: (ad: Ad, subtype?: ChildAdType) => void;
    onRenameAd: (ad: Ad) => void;
    onDeleteAd: (ad: Ad) => void;
    onDuplicateAd: (ad: Ad) => void;
    renamingAdId: number | null;
    deletingAdId: number | null;
    duplicatingAdId: number | null;
    creatingChildForParentId: number | null;
    resolvedMedia?: Record<number, ResolvedAdContent | null>;
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
    organizationId?: number;
}) {
    const isContainer = ad.type === "container";
    const [open, setOpen] = useState(true);
    // Declared here, above the early returns for leaf content forms, so the hook order
    // is the same on every render.
    const [pickingCover, setPickingCover] = useState(false);
    const [pickedCover, setPickedCover] = useState<string | null>(null);
    useEffect(() => () => { if (pickedCover) URL.revokeObjectURL(pickedCover); }, [pickedCover]);

    if (isContainer) {
        const children = ad.children || [];
        const subtype = ad.subtype?.toLowerCase();
        const needsSubtypePicker = !subtype || subtype === "container";
        const containerChildren = children.filter((child) => child.type === "container");
        const leafChildren = children.filter((child) => child.type !== "container");
        return (
            <div className="ceh-accordion" style={{marginLeft: depth > 0 ? `${depth * 14}px` : undefined}}>
                <div
                    className="ceh-accordion-header"
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => setOpen((prev) => !prev)}
                    onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        setOpen((prev) => !prev);
                    }}
                >
                    <span className="ceh-accordion-caret">{open ? "v" : ">"}</span>
                    <span className="ceh-accordion-title">{containerTitle(ad)}</span>
                    {/*
                        Removing a slot that holds nothing (MPN-1889).

                        A container had no delete anywhere -- the menu lives on the leaf
                        cards, and this header only ever offered "+". So an empty shell left
                        behind by a false start could not be taken out from either the
                        Content Catalog or an engagement; it just sat in the ad, looking like
                        a slot somebody meant to fill, until it reached publish.

                        Offered only while the container is empty. Deleting one with children
                        in it would take the content with it, and that is a different and much
                        more dangerous gesture than tidying up a shell.

                        The ad id is in the label because the title is not unique: it is the
                        subtype, so an ad with two empty video slots would otherwise give two
                        identically named buttons.
                    */}
                    {children.length === 0 && (
                        <button
                            type="button"
                            className="ceh-container-delete"
                            title="Remove this empty container"
                            aria-label={`Remove the empty ${containerTitle(ad)} container (Ad ${ad.id})`}
                            disabled={deletingAdId !== null || renamingAdId !== null}
                            onClick={(event) => {
                                event.stopPropagation();
                                onDeleteAd(ad);
                            }}
                        >
                            {deletingAdId === ad.id ? "..." : "Remove"}
                        </button>
                    )}
                    {needsSubtypePicker ? (
                        <div
                            className="ceh-add-child-wrap"
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <button
                                type="button"
                                className="ceh-add-child-btn"
                                onClick={(event) => {
                                    event.stopPropagation();
                                }}
                                disabled={creatingChildForParentId !== null}
                            >
                                {creatingChildForParentId === ad.id ? "..." : "+"}
                            </button>
                            <div className="ceh-add-child-menu">
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "video");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    Video
                                </button>
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "image");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    Image
                                </button>
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "image");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    Document
                                </button>
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "sms");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    SMS
                                </button>
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "direct_mail");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    Mail Piece
                                </button>
                                <button
                                    type="button"
                                    className="ceh-add-child-option"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onAddChild(ad, "email");
                                    }}
                                    disabled={creatingChildForParentId !== null}
                                >
                                    Email
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="ceh-add-child-btn"
                            onClick={(event) => {
                                event.stopPropagation();
                                onAddChild(ad);
                            }}
                            disabled={creatingChildForParentId !== null}
                        >
                            {creatingChildForParentId === ad.id ? "..." : "+"}
                        </button>
                    )}
                </div>

                {open && (
                    <div className="ceh-accordion-body">
                        {!needsSubtypePicker && leafChildren.length > 0 && (
                            <AdCopyForm leafAds={leafChildren} requestEdit={requestEdit} organizationId={organizationId} />
                        )}
                        {children.length === 0 && (
                            <div className="ceh-empty">No child ads in this container.</div>
                        )}
                        {containerChildren.map((child) => (
                            <AdHierarchyNode
                                key={child.id}
                                ad={child}
                                depth={depth + 1}
                                loadingAdId={loadingAdId}
                                selectedAdId={selectedAdId}
                                onSelectAd={onSelectAd}
                                onAddChild={onAddChild}
                                onRenameAd={onRenameAd}
                                onDeleteAd={onDeleteAd}
                                onDuplicateAd={onDuplicateAd}
                                renamingAdId={renamingAdId}
                                deletingAdId={deletingAdId}
                                duplicatingAdId={duplicatingAdId}
                                creatingChildForParentId={creatingChildForParentId}
                                resolvedMedia={resolvedMedia}
                                requestEdit={requestEdit}
                                organizationId={organizationId}
                            />
                        ))}
                        {leafChildren.length > 0 && (
                            <div className="ceh-ad-row">
                                {leafChildren.map((child) => (
                                    <AdHierarchyNode
                                        key={child.id}
                                        ad={child}
                                        depth={depth + 1}
                                        loadingAdId={loadingAdId}
                                        selectedAdId={selectedAdId}
                                        onSelectAd={onSelectAd}
                                        onAddChild={onAddChild}
                                        onRenameAd={onRenameAd}
                                        onDeleteAd={onDeleteAd}
                                        onDuplicateAd={onDuplicateAd}
                                        renamingAdId={renamingAdId}
                                        deletingAdId={deletingAdId}
                                        duplicatingAdId={duplicatingAdId}
                                        creatingChildForParentId={creatingChildForParentId}
                                        resolvedMedia={resolvedMedia}
                                        requestEdit={requestEdit}
                                        organizationId={organizationId}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // A mail piece, an email and a text carry their own body and have no variants, so each
    // edits in place here rather than opening Create Studio. Without this they fell through
    // to the card below and rendered "Not editable in Create Editor", which is what Rachel
    // hit: the ad existed, and there was no way to put anything in it.
    //
    // These blocks used to sit in the container branch above, where they could never run —
    // a container's type is "container", never "direct_mail".
    if (LEAF_CONTENT_FORMS[ad.type]) {
        const Form = LEAF_CONTENT_FORMS[ad.type];
        return (
            <div className="ceh-accordion" style={{ marginLeft: depth > 0 ? `${depth * 14}px` : undefined }}>
                <Form
                    ad={ad}
                    requestEdit={requestEdit}
                    organizationId={organizationId}
                    onEditSide={ad.type === "direct_mail"
                        ? (side: "front" | "back") => onSelectAd(ad, side)
                        : undefined}
                />
            </div>
        );
    }

    const editable = isEditableAd(ad);
    const isLoading = loadingAdId === ad.id;
    const isRenaming = renamingAdId === ad.id;
    const isDeleting = deletingAdId === ad.id;
    const isDuplicating = duplicatingAdId === ad.id;
    const isBusy = loadingAdId !== null || isDeleting || isRenaming || isDuplicating;

    // The cover control and its modal are siblings of the card button, never children:
    // the card IS a <button>, so a nested button is invalid markup and every click inside
    // the modal would bubble out and switch the ad being edited (MPN-1761).
    const resolvedForAd = resolvedMedia?.[ad.id];
    const coverVideoUrl = resolvedForAd?.media?.storage_uri;
    const isVideoAd = !!resolvedForAd?.media?.file_type?.startsWith("video");
    const existingCover = (resolvedForAd?.ad?.content as { thumbnail?: { storage_uri?: string } } | undefined)?.thumbnail;

    const adCard = (
        <button
            type="button"
            className={[
                "ceh-ad-card",
                editable ? "" : "ceh-ad-card--disabled",
                selectedAdId === ad.id ? "ceh-ad-card--selected" : "",
                isLoading ? "ceh-ad-card--loading" : "",
                isDeleting ? "ceh-ad-card--loading" : "",
                isDuplicating ? "ceh-ad-card--loading" : "",
            ].join(" ").trim()}
            onClick={() => {
                if (!editable || isBusy) return;
                onSelectAd(ad);
            }}
            disabled={isBusy}
        >
            <div className="ceh-ad-card-thumb">
                <AdThumb resolved={resolvedForAd} pickedCover={pickedCover}/>
            </div>
            <div className="ceh-ad-card-info">
                <span className="ceh-ad-card-name">{ad.name || `Ad ${ad.id}`}</span>
                <span className="ceh-ad-card-meta">{adTypeLabel(ad)}</span>
                {!editable && (
                    <span className="ceh-ad-card-note">Not editable in Create Editor</span>
                )}
                {isLoading && (
                    <span className="ceh-ad-card-note">Loading editor...</span>
                )}
                {isDeleting && (
                    <span className="ceh-ad-card-note">Deleting...</span>
                )}
                {isRenaming && (
                    <span className="ceh-ad-card-note">Renaming...</span>
                )}
                {isDuplicating && (
                    <span className="ceh-ad-card-note">Duplicating...</span>
                )}
            </div>
        </button>
    );

    const withCover = (
        <div className="ceh-ad-card-wrap">
            {adCard}
            {isVideoAd && coverVideoUrl && (
                <button
                    type="button"
                    className={`ad-thumb-cover${existingCover || pickedCover ? "" : " ad-thumb-cover--none"}`}
                    title={existingCover || pickedCover
                        ? "This frame is the cover sent to the network. Click to change it."
                        : "No cover yet. Click to choose the frame the network shows."}
                    onClick={(e) => { e.stopPropagation(); setPickingCover(true); }}
                >
                    {existingCover || pickedCover ? "Cover" : "Set cover"}
                </button>
            )}
            {pickingCover && coverVideoUrl && (
                <CoverPicker
                    adId={ad.id}
                    videoUrl={coverVideoUrl}
                    adName={ad.name}
                    onSaved={(previewUrl) => {
                        setPickedCover((prev) => { if (prev) URL.revokeObjectURL(prev); return previewUrl; });
                        setPickingCover(false);
                    }}
                    onClose={() => setPickingCover(false)}
                    className="ceh-nested-modal"
                    backdropClassName="ceh-nested-backdrop"
                    style={{zIndex: 1400}}
                />
            )}
        </div>
    );

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger asChild>{withCover}</ContextMenu.Trigger>
            <ContextMenu.Portal>
                <ContextMenu.Content className="radix-context-menu-content" alignOffset={2} style={{zIndex: 2000}}>
                    <ContextMenu.Item
                        className="radix-context-menu-item"
                        onSelect={() => onRenameAd(ad)}
                        disabled={isDeleting || renamingAdId !== null || isDuplicating}
                    >
                        Rename Ad
                    </ContextMenu.Item>
                    <ContextMenu.Item
                        className="radix-context-menu-item"
                        onSelect={() => onDuplicateAd(ad)}
                        disabled={isDeleting || renamingAdId !== null || duplicatingAdId !== null}
                    >
                        Duplicate Ad
                    </ContextMenu.Item>
                    {resolvedMedia?.[ad.id]?.media?.storage_uri && (
                        <ContextMenu.Item
                            className="radix-context-menu-item"
                            onSelect={() => {
                                const media = resolvedMedia![ad.id]!.media!;
                                fetch(media.storage_uri!)
                                    .then((res) => res.blob())
                                    .then((blob) => {
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        const urlExt = media.storage_uri?.split("?")[0].split(".").pop();
                                        const mimeExt = media.file_type?.split("/")[1];
                                        const ext = urlExt || mimeExt || "bin";
                                        const baseName = media.name || ad.name || "media";
                                        a.download = baseName.endsWith(`.${ext}`) ? baseName : `${baseName}.${ext}`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    });
                            }}
                        >
                            Download {ad.type === "video" ? "Video" : "Image"}
                        </ContextMenu.Item>
                    )}
                    <ContextMenu.Item
                        className="radix-context-menu-item radix-context-menu-item--danger"
                        onSelect={() => onDeleteAd(ad)}
                        disabled={isDeleting || renamingAdId !== null || isDuplicating}
                    >
                        Delete Ad
                    </ContextMenu.Item>
                </ContextMenu.Content>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    );
}

export default function CreateEditorModal({
                                              ad = null,
                                              adId,
                                              brandAssets: _brandAssets,
                                              adMode = false,
                                              modalOpen = true,
                                              setModalOpen = () => {
                                              },
                                              onCreate: _onCreate = () => {
                                              },
                                              onDuplicate: _onDuplicate = () => {
                                              },
                                              onDelete: _onDelete = () => {
                                              },
                                              onRename: _onRename = () => {
                                              },
                                              hideX = false,
                                              organizationId,
                                              adGroupId,
                                          }: CreateProps) {
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const mediaService = useMemo(() => new MediaService(), []);
    const [rootAd, setRootAd] = useState<Ad | null>(ad);
    const [selectedAdId, setSelectedAdId] = useState<number | null>(null);
    const [loadingHierarchy, setLoadingHierarchy] = useState(false);
    const [loadingAdId, setLoadingAdId] = useState<number | null>(null);
    const [renamingAdId, setRenamingAdId] = useState<number | null>(null);
    const [adPendingRename, setAdPendingRename] = useState<Ad | null>(null);
    const [deletingAdId, setDeletingAdId] = useState<number | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [adPendingDelete, setAdPendingDelete] = useState<Ad | null>(null);
    const [creatingChildForParentId, setCreatingChildForParentId] = useState<number | null>(null);
    // Why adding an ad failed. Rendered rather than only logged: a swallowed 422 is
    // indistinguishable from a dead button, which is exactly how MPN-1770 was reported.
    const [createError, setCreateError] = useState<string | null>(null);
    const [duplicatingAdId, setDuplicatingAdId] = useState<number | null>(null);
    const [hierarchyError, setHierarchyError] = useState<string | null>(null);
    const [editorResolvedMedia, setEditorResolvedMedia] = useState<Record<number, ResolvedAdContent | null>>({});
    const [debugOpen, setDebugOpen] = useState(false);
    const [convertingAdId, setConvertingAdId] = useState<number | null>(null);
    const isAdmin = appStore.loggedInUser?.permissions?.is_super_user ?? false;

    // ─── Content lock (MPN-1390) ───
    // Every content edit in here funnels through requestEdit. When the tree is approved or
    // published the server refuses the edit (409 content_locked), so we hold the action,
    // ask, and then either re-run it with force_edit or fork and start over on the copy.
    const [pendingEdit, setPendingEdit] = useState<((force: boolean) => void | Promise<void>) | null>(null);
    const [forking, setForking] = useState(false);
    const [lockError, setLockError] = useState<string | null>(null);
    const lock = rootAd?.lock ?? null;

    const requestEdit = useCallback((run: (force: boolean) => void | Promise<void>) => {
        if (contentEditAllowed(lock, false)) {
            void run(false);
            return;
        }
        setLockError(null);
        // Stored via updater form — a bare setState(fn) would call it as a reducer.
        setPendingEdit(() => run);
    }, [lock]);


    // Takes an explicit root id so a fork can reload the modal onto the copy, which is a
    // different ad than the `ad`/`adId` props point at.
    const loadHierarchyFor = useCallback(async (rootAdId: number) => {
        setLoadingHierarchy(true);
        setHierarchyError(null);
        try {
            const hierarchy = await ccService.getAd(rootAdId, ["children"]);
            setRootAd(hierarchy);

            const ids = collectAdIdsFromHierarchy(hierarchy);
            if (ids.length > 0) {
                try {
                    const result = await mediaService.resolveAdDisplay(ids);
                    setEditorResolvedMedia(result.resolved);
                } catch { /* non-critical */
                }
            }
        } catch (error) {
            console.error("Failed to load ad hierarchy:", error);
            setRootAd(null);
            setHierarchyError("Failed to load ad hierarchy.");
        } finally {
            setLoadingHierarchy(false);
        }
    }, [ccService, mediaService]);

    const loadHierarchy = useCallback(async () => {
        const rootAdId = adId ?? ad?.id ?? null;
        if (rootAdId === null) {
            setRootAd(null);
            setHierarchyError("No ad selected.");
            return;
        }

        if (!adId && ad && ad.id === rootAdId) {
            setRootAd(ad);
            setHierarchyError(null);
            const ids = collectAdIdsFromHierarchy(ad);
            if (ids.length > 0) {
                try {
                    const result = await mediaService.resolveAdDisplay(ids);
                    setEditorResolvedMedia(result.resolved);
                } catch { /* non-critical */
                }
            }
            return;
        }

        await loadHierarchyFor(rootAdId);
    }, [ad, adId, loadHierarchyFor, mediaService]);

    const handleForkAndEdit = useCallback(async () => {
        if (!rootAd) return;
        setForking(true);
        setLockError(null);
        try {
            const fork = await ccService.forkAd(rootAd.id, adGroupId);
            setPendingEdit(null);
            setSelectedAdId(null);
            // The fork API returns the new root, not the counterpart of whichever leaf we
            // were on, so land on the copy's tree and let the user pick the leaf again.
            await loadHierarchyFor(fork.id);
            showMessage("Copy created — you're now editing the copy", "success", 4000);
        } catch (error) {
            console.error("Failed to fork ad:", error);
            setLockError(error instanceof Error ? error.message : "Failed to make a copy.");
        } finally {
            setForking(false);
        }
    }, [rootAd, adGroupId, ccService, loadHierarchyFor]);

    useEffect(() => {
        if (!modalOpen) {
            setSelectedAdId(null);
            setLoadingAdId(null);
            setRenamingAdId(null);
            setAdPendingRename(null);
            setDeletingAdId(null);
            setAdPendingDelete(null);
            setCreatingChildForParentId(null);
            setDuplicatingAdId(null);
            setHierarchyError(null);
            setEditorResolvedMedia({});
            return;
        }

        setSelectedAdId(null);
        setLoadingAdId(null);
        setRenamingAdId(null);
        setAdPendingRename(null);
        setDeletingAdId(null);
        setAdPendingDelete(null);
        setCreatingChildForParentId(null);
        setDuplicatingAdId(null);
        setEditorResolvedMedia({});
        void loadHierarchy();
    }, [loadHierarchy, modalOpen]);

    const openInKinema = useCallback(async (nextAd: Ad, side?: "front" | "back") => {
        setConvertingAdId(nextAd.id);
        try {
            const fullAd = await ccService.getAd(nextAd.id, ["content"]);
            const content = fullAd.content as any;

            /*
                A mail piece has a composition per side rather than one for the ad, and the
                server creates the side's on first open. Nothing to convert, so it skips the
                legacy-layer branch entirely.
            */
            if (fullAd.type === "direct_mail") {
                setConvertingAdId(null);
                const params = new URLSearchParams();
                params.set('side', side ?? 'front');
                if (organizationId != null) params.set('organizationId', String(organizationId));
                navigate(`/kinema/ads/${nextAd.id}?${params.toString()}`);
                return;
            }

            if (content?.layered_media_id) {
                // Already has layered media — go straight to Kinema
            } else if (content?.composition_layer || content?.layer || content?.type === 'composition') {
                const layerPayload = content.composition_layer ?? content.layer ?? content;
                const layerId = layerPayload?.id;
                if (layerId) {
                    await convertLegacyLayer(String(layerId));
                }
            }

            setConvertingAdId(null);
            const params = new URLSearchParams();
            if (organizationId != null) params.set('organizationId', String(organizationId));
            const qs = params.toString();
            navigate(`/kinema/ads/${nextAd.id}${qs ? `?${qs}` : ''}`);
        } catch (err) {
            console.error("Failed to convert layer:", err);
            showMessage("Failed to convert to Kinema format", "error", 5000);
            setConvertingAdId(null);
        }
    }, [ccService, organizationId]);

    const handleAdSelect = useCallback(async (nextAd: Ad, side?: "front" | "back") => {
        const isMailSide = nextAd.type === "direct_mail" && !!side;
        if ((!isEditableAd(nextAd) && !isMailSide)
            || loadingAdId !== null || convertingAdId !== null) return;
        // Opening Kinema is the gesture, but the edit lands on LayeredMedia.data after we
        // navigate away — so Kinema gates its own save. Confirming here only gets you in.
        requestEdit(() => openInKinema(nextAd, side));
    }, [loadingAdId, convertingAdId, requestEdit, openInKinema]);

    const createChildContainer = useCallback(async (parentAd: Ad, subtype: ChildAdType) => {
        setCreatingChildForParentId(parentAd.id);
        setCreateError(null);
        try {
            const isLeaf = LEAF_TYPES.has(subtype);
            const payload: CreateAdPayload = {
                name: isLeaf ? LEAF_NAMES[subtype] : capitalizeFirstLetter(subtype),
                type: isLeaf ? subtype : "container",
                // A leaf carries its own content and contains nothing, so it has no subtype.
                subtype: isLeaf ? null : subtype,
                is_template: false,
                parent_id: parentAd.id,
            };
            const created = await ccService.createAd(payload);
            setRootAd((prev) => addChildToTree(prev, parentAd.id, new Ad(created)));
        } catch (error) {
            // Shown, not only logged. This swallowed a 422 for months: clicking Mail Piece,
            // Email or SMS did nothing at all, with no message, and the only trace was a
            // console line nobody had open (MPN-1770).
            console.error("Failed to create child container:", error);
            setCreateError(
                error instanceof Error ? error.message : "Could not add that. Try again.",
            );
        } finally {
            setCreatingChildForParentId(null);
        }
    }, [ccService]);

    const createTypedChild = useCallback(async (parentAd: Ad) => {
        const subtype = parentAd.subtype?.toLowerCase();
        if (!subtype || subtype === "container") return;

        setCreatingChildForParentId(parentAd.id);
        setCreateError(null);
        try {
            const type = subtype as Ad["type"];
            const hasExistingChildren = (parentAd.children || []).some((c) => c.type !== "container");
            const payload: CreateAdPayload = {
                name: `${capitalizeFirstLetter(type)} Ad`,
                type,
                subtype: type,
                is_template: false,
                is_default: !hasExistingChildren,
                parent_id: parentAd.id,
            };
            const created = await ccService.createAd(payload);
            setRootAd((prev) => addChildToTree(prev, parentAd.id, new Ad(created)));
        } catch (error) {
            console.error("Failed to create child ad:", error);
            setCreateError(
                error instanceof Error ? error.message : "Could not add that. Try again.",
            );
        } finally {
            setCreatingChildForParentId(null);
        }
    }, [ccService]);

    const handleAddChild = useCallback((parentAd: Ad, subtype?: ChildAdType) => {
        if (subtype) {
            void createChildContainer(parentAd, subtype);
            return;
        }
        void createTypedChild(parentAd);
    }, [createChildContainer, createTypedChild]);

    const handleDuplicateAd = useCallback(async (adToDuplicate: Ad) => {
        if (duplicatingAdId !== null) return;
        setDuplicatingAdId(adToDuplicate.id);
        try {
            const duplicated = await ccService.duplicateAd(adToDuplicate.id);
            const fullAd = await ccService.getAd(duplicated.id, ["content"]);
            const created = new Ad({...fullAd, parent_id: duplicated.parent_id});
            if (created.parent_id) {
                setRootAd((prev) => addChildToTree(prev, created.parent_id!, created));
            }
            try {
                const result = await mediaService.resolveAdDisplay([created.id]);
                setEditorResolvedMedia((prev) => ({...prev, ...result.resolved}));
            } catch { /* non-critical */
            }
        } catch (error) {
            console.error("Failed to duplicate ad:", error);
        } finally {
            setDuplicatingAdId(null);
        }
    }, [ccService, duplicatingAdId, mediaService]);

    const removeAdFromTree = useCallback((root: Ad | null, adIdToRemove: number): Ad | null => {
        if (!root) return root;
        if (root.id === adIdToRemove) return null;

        const walk = (node: Ad): Ad => {
            const next = new Ad(node);
            next.children = (node.children || [])
                .filter((child) => child.id !== adIdToRemove)
                .map((child) => walk(child));
            return next;
        };

        return walk(root);
    }, []);

    const renameAdInTree = useCallback((root: Ad | null, adIdToRename: number, nextName: string): Ad | null => {
        if (!root) return root;
        const walk = (node: Ad): Ad => {
            const next = new Ad(node);
            if (next.id === adIdToRename) next.name = nextName;
            next.children = (node.children || []).map((child) => walk(child));
            return next;
        };
        return walk(root);
    }, []);

    const handleRenameAd = useCallback(async (nextName: string) => {
        if (!adPendingRename || renamingAdId !== null) return;
        setRenamingAdId(adPendingRename.id);
        try {
            await ccService.updateAd(adPendingRename.id, {name: nextName});
            setRootAd((prev) => renameAdInTree(prev, adPendingRename.id, nextName));
            setAdPendingRename(null);
        } catch (error) {
            console.error("Failed to rename ad:", error);
        } finally {
            setRenamingAdId(null);
        }
    }, [adPendingRename, ccService, renameAdInTree, renamingAdId, selectedAdId]);

    const handleConfirmDeleteAd = useCallback(async () => {
        if (!adPendingDelete || deletingAdId !== null) return;
        setDeletingAdId(adPendingDelete.id);
        setDeleteError(null);
        try {
            await ccService.deleteAd(adPendingDelete.id);
            setRootAd((prev) => removeAdFromTree(prev, adPendingDelete.id));

            if (selectedAdId === adPendingDelete.id) {
                setSelectedAdId(null);
            }
            setAdPendingDelete(null);
        } catch (error) {
            // Kept on screen rather than only in the console: a refused delete used to leave
            // the dialog sitting there saying nothing, which reads as a dead button (MPN-1889).
            setDeleteError(error instanceof Error ? error.message : "Failed to delete.");
        } finally {
            setDeletingAdId(null);
        }
    }, [adPendingDelete, ccService, deletingAdId, removeAdFromTree, selectedAdId]);

    return (
        <MotiModal isOpen={modalOpen} setIsOpen={setModalOpen} hideX={hideX}>
            <MotiModal.Header>
                <div className="ceh-header">
                    <span className="ceh-header-ad-name">{rootAd?.name || ""}</span>
                    <RecordId label="Ad" id={rootAd?.id} />
                </div>
            </MotiModal.Header>
            <MotiModal.Body>
                <div className="ceh-select-layout">
                        {adMode ? (
                            <ResizableSplitView
                                className="ceh-select-split"
                                storageKey="createEditor.reviewPanel.width"
                                defaultLeftSize={280}
                                minLeftSize={200}
                                minRightSize={400}
                                left={
                                    /* Tags above the comments (MPN-1506): opened from an
                                       engagement this showed comments only, so the same ad
                                       looked untagged here and tagged in the catalog. */
                                    <div className="ceh-ad-side">
                                        {rootAd && <AdTagsReadOnly adId={rootAd.id} />}
                                        {/* Between the tags and the comments, which is
                                            where the rebrander is already looking, and
                                            what the ticket's mockup shows (MPN-1622). */}
                                        <CustomizationRequests adId={rootAd?.id} />
                                        <ReviewCommentList
                                            rootAd={rootAd}
                                            /* MPN-1454. The server authorizes this too --
                                               through the review todo, which is where
                                               "may this person comment" already lives. The
                                               check here only decides whether to offer the
                                               box. */
                                            canComment={appStore.loggedInUser?.permissions?.hasAccess(
                                                "Comment on GuidePosts",
                                                PermissionLevels.CREATE,
                                            )}
                                        />
                                    </div>
                                }
                                right={
                                    <div className="ceh-selector">
                                        {loadingHierarchy && (
                                            <div className="ceh-state">Loading ad hierarchy...</div>
                                        )}

                                        {createError && (
                                            <div
                                                className="alert alert-danger py-2 px-3 mb-2"
                                                style={{ fontSize: 12 }}
                                                role="alert"
                                            >
                                                {createError}
                                                <button
                                                    type="button"
                                                    className="btn-close float-end"
                                                    style={{ fontSize: 10 }}
                                                    aria-label="Dismiss"
                                                    onClick={() => setCreateError(null)}
                                                />
                                            </div>
                                        )}

                                        {!loadingHierarchy && hierarchyError && (
                                            <div className="ceh-state">
                                                <p className="mb-2">{hierarchyError}</p>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        void loadHierarchy();
                                                    }}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}

                                        {!loadingHierarchy && !hierarchyError && rootAd && (
                                            <AdHierarchyNode
                                                ad={rootAd}
                                                loadingAdId={loadingAdId}
                                                selectedAdId={selectedAdId}
                                                onSelectAd={handleAdSelect}
                                                onAddChild={handleAddChild}
                                                onRenameAd={(nextAd) => {
                                                    setAdPendingRename(nextAd);
                                                }}
                                                onDeleteAd={(nextAd) => {
                                                    setDeleteError(null);
                                                    setAdPendingDelete(nextAd);
                                                }}
                                                onDuplicateAd={(nextAd) => {
                                                    void handleDuplicateAd(nextAd);
                                                }}
                                                renamingAdId={renamingAdId}
                                                deletingAdId={deletingAdId}
                                                duplicatingAdId={duplicatingAdId}
                                                creatingChildForParentId={creatingChildForParentId}
                                                resolvedMedia={editorResolvedMedia}
                                                requestEdit={requestEdit}
                                                organizationId={organizationId}
                                            />
                                        )}

                                        {!loadingHierarchy && !hierarchyError && !rootAd && (
                                            <div className="ceh-state">No ad selected.</div>
                                        )}
                                    </div>
                                }
                            />
                        ) : (
                            <ResizableSplitView
                                className="ceh-select-split"
                                storageKey="createEditor.tagPanel.width"
                                defaultLeftSize={300}
                                minLeftSize={200}
                                minRightSize={400}
                                left={
                                    rootAd ? (
                                        <AdTagsPanel adId={rootAd.id} adName={rootAd.name || `Ad ${rootAd.id}`}/>
                                    ) : (
                                        <div className="ceh-tags-panel">
                                            <div className="ceh-tags-panel-empty">No ad selected</div>
                                        </div>
                                    )
                                }
                                right={
                                    <div className="ceh-selector">
                                        {loadingHierarchy && (
                                            <div className="ceh-state">Loading ad hierarchy...</div>
                                        )}

                                        {!loadingHierarchy && hierarchyError && (
                                            <div className="ceh-state">
                                                <p className="mb-2">{hierarchyError}</p>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        void loadHierarchy();
                                                    }}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}

                                        {!loadingHierarchy && !hierarchyError && rootAd && (
                                            <AdHierarchyNode
                                                ad={rootAd}
                                                loadingAdId={loadingAdId}
                                                selectedAdId={selectedAdId}
                                                onSelectAd={handleAdSelect}
                                                onAddChild={handleAddChild}
                                                onRenameAd={(nextAd) => {
                                                    setAdPendingRename(nextAd);
                                                }}
                                                onDeleteAd={(nextAd) => {
                                                    setDeleteError(null);
                                                    setAdPendingDelete(nextAd);
                                                }}
                                                onDuplicateAd={(nextAd) => {
                                                    void handleDuplicateAd(nextAd);
                                                }}
                                                renamingAdId={renamingAdId}
                                                deletingAdId={deletingAdId}
                                                duplicatingAdId={duplicatingAdId}
                                                creatingChildForParentId={creatingChildForParentId}
                                                resolvedMedia={editorResolvedMedia}
                                                requestEdit={requestEdit}
                                                organizationId={organizationId}
                                            />
                                        )}

                                        {!loadingHierarchy && !hierarchyError && !rootAd && (
                                            <div className="ceh-state">No ad selected.</div>
                                        )}
                                    </div>
                                }
                            />
                        )}
                    </div>
                {isAdmin && rootAd && (
                    <>
                        <button
                            type="button"
                            onClick={() => setDebugOpen(true)}
                            title="Debug Ad Tree"
                            style={{
                                position: "absolute",
                                bottom: 20,
                                right: 20,
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                background: "#6c757d",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "1.1rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                zIndex: 10,
                            }}
                        >
                            {"{}"}
                        </button>
                        <DebugAdTreeModal
                            show={debugOpen}
                            onClose={() => setDebugOpen(false)}
                            rootAd={rootAd}
                            onAdUpdated={() => { void loadHierarchy(); }}
                        />
                    </>
                )}
                <AdLockedDialog
                    show={pendingEdit !== null}
                    adName={rootAd?.name || undefined}
                    lock={lock}
                    replacesInAdGroup={adGroupId != null}
                    onCopy={() => {
                        void handleForkAndEdit();
                    }}
                    onEditAnyway={() => {
                        const run = pendingEdit;
                        setPendingEdit(null);
                        void run?.(true);
                    }}
                    onClose={() => {
                        if (!forking) setPendingEdit(null);
                    }}
                    isWorking={forking}
                    error={lockError}
                />
                <ConfirmActionModal
                    show={adPendingDelete !== null}
                    title="Delete Ad"
                    message={adPendingDelete
                        ? `Delete "${adPendingDelete.name || `Ad ${adPendingDelete.id}`}"?`
                        : ""}
                    confirmLabel="Delete"
                    onConfirm={() => {
                        void handleConfirmDeleteAd();
                    }}
                    onClose={() => {
                        if (deletingAdId === null) {
                            setAdPendingDelete(null);
                            setDeleteError(null);
                        }
                    }}
                    isLoading={deletingAdId !== null}
                    error={deleteError}
                    className="ceh-nested-modal"
                    backdropClassName="ceh-nested-backdrop"
                    style={{zIndex: 1400}}
                />
                <RenameActionModal
                    show={adPendingRename !== null}
                    title="Rename Ad"
                    initialValue={adPendingRename?.name || (adPendingRename ? `Ad ${adPendingRename.id}` : "")}
                    confirmLabel="Rename"
                    onConfirm={(nextName) => {
                        void handleRenameAd(nextName);
                    }}
                    onClose={() => {
                        if (renamingAdId === null) setAdPendingRename(null);
                    }}
                    isLoading={renamingAdId !== null}
                    className="ceh-nested-modal"
                    backdropClassName="ceh-nested-backdrop"
                    style={{zIndex: 1400}}
                />
            </MotiModal.Body>
        </MotiModal>
    );
}
